import {
  Avatar,
  Badge,
  Flex,
  Tab,
  Table,
  TableContainer,
  TabList,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterField, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { SearchableSelect } from '../../../components/Select'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatApiDate, formatDateTime, formatPhone, useAppToast, useHasRole } from '../../../helpers'
import { EmployeeService } from '../../../services/Employee/EmployeeService'
import { IEmployeeRowRaw, TEmployeeStatus } from '../../../services/Employee/types'
import { EMPLOYEE_STATUS, EMPLOYEE_STATUSES } from './employeeConstants'
import { EmployeeCreateModal } from './EmployeeCreateModal'
import { EmployeeViewModal } from './EmployeeViewModal'
import { useEmployeeOptions } from './useEmployeeOptions'

interface IFilters {
  search: string
  sectorId: string
  jobPositionId: number | null
}

const INITIAL_FILTERS: IFilters = { search: '', sectorId: '', jobPositionId: null }

/** The dates each tab shows after the common columns. */
const DATE_COLUMNS: Record<TEmployeeStatus, { label: string; value: (row: IEmployeeRowRaw) => string }[]> = {
  pending: [{ label: 'Enviado em', value: (row) => formatDateTime(row.submitted_at) }],
  active: [{ label: 'Admissão', value: (row) => formatApiDate(row.admission_date) }],
  inactive: [
    { label: 'Admissão', value: (row) => formatApiDate(row.admission_date) },
    { label: 'Desligamento', value: (row) => formatApiDate(row.termination_date) }
  ]
}

/**
 * "Colaboradores" (ABRA-320): pending admissions, active and inactive collaborators, in tabs over
 * one table so the search and the filters stay when switching. `?colaborador=ID` opens the sheet.
 */
export default function EmployeesPage() {
  const service = useMemo(() => new EmployeeService(), [])
  const toast = useAppToast()
  const hasRole = useHasRole()
  const canAdd = hasRole('add_employees')
  const { roles, sectors, roleOptions } = useEmployeeOptions()
  const [searchParams, setSearchParams] = useSearchParams()

  const [tabIndex, setTabIndex] = useState(0)
  const status = EMPLOYEE_STATUSES[tabIndex]
  const [filters, setFilters] = useState<IFilters>(INITIAL_FILTERS)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [rows, setRows] = useState<IEmployeeRowRaw[]>([])
  const [counts, setCounts] = useState<Record<TEmployeeStatus, number>>({ pending: 0, active: 0, inactive: 0 })
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [viewingId, setViewingId] = useState<number | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const refreshList = () => {
    setLoading(true)
    setLoadError(false)
    service
      .list(
        status,
        {
          search: filters.search,
          sector_id: filters.sectorId ? Number(filters.sectorId) : null,
          job_position_id: filters.jobPositionId
        },
        page,
        pageSize
      )
      .then((result) => {
        setRows(result.data)
        setCounts(result.counts)
        setTotalPages(result.last_page)
      })
      .catch((error) => {
        setLoadError(true)
        toast.error('Erro ao carregar os colaboradores', error)
      })
      .finally(() => setLoading(false))
  }

  useEffect(refreshList, [status, filters, page, pageSize])

  // Deep link: /colaboradores?colaborador={id}
  useEffect(() => {
    const id = Number(searchParams.get('colaborador'))
    if (id) setViewingId(id)
  }, [searchParams])

  const updateFilters = (patch: Partial<IFilters>) => {
    setFilters((current) => ({ ...current, ...patch }))
    setPage(1)
  }

  const closeView = () => {
    setViewingId(null)
    if (searchParams.has('colaborador')) setSearchParams({}, { replace: true })
  }

  const dateColumns = DATE_COLUMNS[status]
  const colSpan = 7 + dateColumns.length

  return (
    <>
      <PageTitle title="Colaboradores" description="Admissões pendentes e colaboradores ativos e inativos" />

      <FilterBar
        onClear={() => updateFilters(INITIAL_FILTERS)}
        isDirty={JSON.stringify(filters) !== JSON.stringify(INITIAL_FILTERS)}
      >
        <FilterSearch
          value={filters.search}
          onChange={(search) => updateFilters({ search })}
          placeholder="nome / usuário"
        />
        <FilterSelect
          label="Setor"
          value={filters.sectorId}
          onChange={(sectorId) => updateFilters({ sectorId })}
          options={sectors}
        />
        <FilterField label="Cargo">
          <SearchableSelect<number>
            size="sm"
            isClearable
            options={roleOptions}
            value={filters.jobPositionId ?? ''}
            onChange={(jobPositionId) => updateFilters({ jobPositionId: jobPositionId === '' ? null : jobPositionId })}
            placeholder="Todos"
            entity="cargo"
          />
        </FilterField>
      </FilterBar>

      <Tabs
        index={tabIndex}
        onChange={(index) => {
          setTabIndex(index)
          setPage(1)
        }}
        mb={4}
      >
        <TabList overflowX="auto" overflowY="hidden">
          {EMPLOYEE_STATUSES.map((tabStatus) => (
            <Tab key={tabStatus} whiteSpace="nowrap">
              {EMPLOYEE_STATUS[tabStatus].tab}
              <Badge ml={2}>{counts[tabStatus]}</Badge>
            </Tab>
          ))}
        </TabList>
      </Tabs>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Colaborador</Th>
              <Th>Usuário</Th>
              <Th>Cargo</Th>
              <Th>Setor</Th>
              <Th>E-mail</Th>
              <Th>Telefone</Th>
              {dateColumns.map((column) => (
                <Th key={column.label}>{column.label}</Th>
              ))}
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={colSpan} />
            ) : loadError ? (
              <TableErrorRow colSpan={colSpan} entity="colaboradores" onRetry={refreshList} />
            ) : rows.length === 0 ? (
              <TableEmptyRow colSpan={colSpan} entity="colaborador" />
            ) : (
              rows.map((row) => (
                <Tr key={row.id}>
                  <Td>
                    <Flex align="center" gap={2}>
                      <Avatar size="sm" src={row.photo_url ?? undefined} name={row.name ?? undefined} />
                      <Text>{row.name}</Text>
                    </Flex>
                  </Td>
                  <Td>
                    <EmptyCell value={row.username} />
                  </Td>
                  <Td>
                    <EmptyCell value={row.position} />
                  </Td>
                  <Td>
                    <EmptyCell value={row.sector} />
                  </Td>
                  <Td>{row.email}</Td>
                  <Td>
                    <EmptyCell value={formatPhone(row.phone)} />
                  </Td>
                  {dateColumns.map((column) => (
                    <Td key={column.label}>
                      <EmptyCell value={column.value(row)} />
                    </Td>
                  ))}
                  <Td>
                    <ActionButton action="view" onClick={() => setViewingId(row.id)} />
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={setPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
      />

      {canAdd && <ActionFab label="Adicionar colaborador" onClick={() => setIsCreateOpen(true)} />}

      <EmployeeViewModal
        isOpen={viewingId !== null}
        onClose={closeView}
        employeeId={viewingId}
        roles={roles}
        sectors={sectors}
        onChanged={refreshList}
      />

      <EmployeeCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        roles={roles}
        sectors={sectors}
        onCreated={(employee) => {
          // On another tab, switching to "Ativos" already fetches it.
          if (status === 'active') refreshList()
          else {
            setTabIndex(EMPLOYEE_STATUSES.indexOf('active'))
            setPage(1)
          }
          setViewingId(employee.id)
        }}
      />
    </>
  )
}
